import { makeStyles, tokens, Button, Text, Tooltip } from '@fluentui/react-components';
import { DismissRegular, WarningRegular } from '@fluentui/react-icons';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { RunStatusBadge } from '@/components/MonitoringView/RunsSidebar';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import type { RunStatus } from '@/types/monitoring';

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground4,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    gap: tokens.spacingHorizontalM,
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flex: 1,
  },
  runInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    overflow: 'hidden',
  },
  runId: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  warningIcon: {
    color: tokens.colorPaletteYellowForeground1,
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
});

interface ExecutionModeBannerProps {
  workflow: WorkflowDefinition | null;
  /** Called when user clicks Exit - clears execution and allows parent to update URL */
  onExit?: (() => void) | undefined;
}

export const ExecutionModeBanner = ({ workflow, onExit }: ExecutionModeBannerProps) => {
  const styles = useStyles();
  const { runState, isExecutionMode, clearExecution, detectDirtyNodes } = useEditorRunStore();

  const handleExit = () => {
    clearExecution();
    onExit?.();
  };

  if (!isExecutionMode || !runState) {
    return null;
  }

  // Detect dirty nodes if workflow is available
  const dirtyResult = workflow ? detectDirtyNodes(workflow) : null;
  const hasDirtyNodes = dirtyResult?.hasDirtyNodes ?? false;

  return (
    <div className={styles.banner} role="status" aria-label="Execution debug mode">
      <div className={styles.info}>
        <div className={styles.runInfo}>
          <RunStatusBadge status={runState.runStatus as RunStatus} size="small" />
          <Text className={styles.runId}>
            {runState.runId}
          </Text>
          {hasDirtyNodes && (
            <Tooltip
              content={`${dirtyResult?.dirtyNodes.length} node(s) have changed since this execution`}
              relationship="label"
            >
              <WarningRegular className={styles.warningIcon} />
            </Tooltip>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          onClick={handleExit}
          aria-label="Exit debug mode"
        >
          Exit
        </Button>
      </div>
    </div>
  );
};
